import React, { useState } from "react";
import { ArrowUp, ArrowRight, Instagram, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { GoodTagline } from "./GoodTagline";
import BrandStripe from "./BrandStripe";
import { TID } from "@/lib/testIds";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    const ok = /\S+@\S+\.\S+/.test(email);
    if (!ok) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("You're on the list. Welcome to Good Eyes.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <footer
      data-testid={TID.footer.root}
      className="relative bg-[color:var(--ge-navy)] text-[color:var(--ge-cream)] pt-24 pb-10 overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-2" style={{ background: "var(--ge-yellow)" }} />

      <div className="ge-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold" style={{ color: "var(--ge-yellow)" }}>
              Newsletter
            </div>
            <h3 className="mt-4 tight-cap font-bold leading-[0.95] text-[clamp(2rem,4.5vw,4rem)]" style={{ color: "var(--ge-cream)" }}>
              Be first to see <span style={{ color: "var(--ge-yellow)" }}>new styles</span>{" "}
              &amp; colourways.
            </h3>
            <div className="mt-4 h-[4px] w-14 rounded-full" style={{ background: "var(--ge-yellow)" }} />
            <p className="mt-6 max-w-md text-base md:text-lg font-light text-[color:var(--ge-cream)]/80 leading-relaxed">
              Occasional letters about new releases, sustainability updates
              and where to try Good Eyes in person. No spam, that&apos;d be
              short-sighted.
            </p>

            <form onSubmit={onSubmit} noValidate className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid={TID.footer.newsletterInput}
                className="flex-1 bg-transparent border border-[color:var(--ge-cream)]/30 focus:border-[color:var(--ge-yellow)] outline-none rounded-full px-5 py-3 text-sm placeholder:text-[color:var(--ge-cream)]/40 text-[color:var(--ge-cream)]"
              />
              <button
                type="submit"
                data-testid={TID.footer.newsletterSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.24em] font-semibold transition-colors hover:brightness-110"
                style={{ background: "var(--ge-yellow)", color: "var(--ge-navy)" }}
              >
                Subscribe <ArrowRight size={14} />
              </button>
            </form>
          </div>

          <div className="md:col-span-5 grid grid-cols-2 gap-8">
            <FooterCol
              title="Shop"
              items={[
                { label: "The Range", href: "/shop" },
                { label: "Insight", href: "/product/insight" },
                { label: "Scope", href: "/product/scope" },
                { label: "Zoom", href: "/product/zoom" },
                { label: "All Styles", href: "/shop" },
              ]}
            />
            <FooterCol
              title="Company"
              items={[
                { label: "About", href: "/about" },
                { label: "Sustainability", href: "/about" },
                { label: "Strengths", href: "/#strengths" },
                { label: "Order Form", href: "/order" },
                { label: "Contact", href: "/contact", tid: TID.footer.linkContact },
              ]}
            />
          </div>
        </div>

        {/* Middle - big brand mark */}
        <div className="mt-20 md:mt-28 border-t border-[color:var(--ge-cream)]/15 pt-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <GoodTagline variant="white" height={100} />
          </div>
          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-3" style={{ color: "var(--ge-yellow)" }}>
              Contact
            </div>
            <ul className="space-y-3 text-[color:var(--ge-cream)]/90">
              <li>
                <a href="tel:+64216070073" className="text-sm hover:text-[color:var(--ge-yellow)] transition-colors inline-flex items-center gap-2">
                  <Phone size={14} /> 021 607 073
                </a>
              </li>
              <li>
                <a href="mailto:sales@goodeyes.co.nz" className="text-sm hover:text-[color:var(--ge-yellow)] transition-colors inline-flex items-center gap-2">
                  <Mail size={14} /> sales@goodeyes.co.nz
                </a>
              </li>
              <li className="text-xs text-[color:var(--ge-cream)]/60 pt-2">
                Designed in Auckland, New Zealand
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 border-t border-[color:var(--ge-cream)]/15 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-cream)]/60">
            <span>© {new Date().getFullYear()} Good Eyes NZ</span>
            <span className="hidden md:inline">Designed in Aotearoa</span>
            <span className="hidden md:inline">Made from 100% recycled materials</span>
          </div>

          <div className="flex items-center gap-3">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid={TID.footer.linkInstagram} className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--ge-cream)]/25 hover:bg-[color:var(--ge-yellow)] hover:text-[color:var(--ge-navy)] hover:border-transparent transition-colors">
              <Instagram size={16} />
            </a>
            <a href="mailto:sales@goodeyes.co.nz" aria-label="Email" className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--ge-cream)]/25 hover:bg-[color:var(--ge-yellow)] hover:text-[color:var(--ge-navy)] hover:border-transparent transition-colors">
              <Mail size={16} />
            </a>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Back to top"
              data-testid={TID.footer.backToTop}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[10px] uppercase tracking-[0.28em] font-semibold transition-colors hover:brightness-110"
              style={{ background: "var(--ge-yellow)", color: "var(--ge-navy)" }}
            >
              Back to top <ArrowUp size={12} />
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <BrandStripe height={10} />
      </div>
    </footer>
  );
};

const FooterCol = ({ title, items }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-4" style={{ color: "var(--ge-yellow)" }}>
      {title}
    </div>
    <ul className="flex flex-col gap-2.5">
      {items.map((it) => {
        const isExternal = it.href.startsWith("mailto") || it.href.startsWith("http") || it.href.startsWith("/#");
        const cls = "text-[13px] text-[color:var(--ge-cream)]/90 hover:text-[color:var(--ge-yellow)] transition-colors cursor-pointer";
        return (
          <li key={it.label}>
            {isExternal ? (
              <a href={it.href} data-testid={it.tid} className={cls}>{it.label}</a>
            ) : (
              <Link to={it.href} data-testid={it.tid} className={cls}>{it.label}</Link>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

export default Footer;
